import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import auditLogService from '../../../services/auditLogService';
import notificationManager from '../../../services/notificationManager';
import recoveryService from '../../../services/recoveryService';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';


const AccountSettings = ({ onChange }) => {
  const { user, userProfile, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    relationshipStatus: '',
    relationshipDuration: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [validationStatus, setValidationStatus] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  // Load user data from AuthContext
  useEffect(() => {
    if (userProfile) {
      const nameParts = userProfile?.full_name?.split(' ') || ['', ''];
      setFormData(prev => ({
        ...prev,
        firstName: nameParts?.[0] || '',
        lastName: nameParts?.slice(1)?.join(' ') || '',
        email: userProfile?.email || user?.email || '',
        dateOfBirth: userProfile?.date_of_birth || '',
        gender: userProfile?.gender || '',
        relationshipStatus: userProfile?.relationship_status || '',
        relationshipDuration: userProfile?.relationship_duration || ''
      }));
      
      // Log profile view when component loads
      if (user?.id) {
        auditLogService?.logProfileView(user?.id, {
          timestamp: new Date()?.toISOString(),
          page: 'account_settings'
        });
      }
      
      // Check for pending sync operations
      if (recoveryService?.hasPendingSync()) {
        recoveryService?.showRecoveryNotification(async () => {
          const cachedUpdate = recoveryService?.getCachedProfileUpdate(user?.id);
          if (cachedUpdate) {
            notificationManager?.info('Attempting to sync cached changes...');
            await updateProfile(cachedUpdate?.updates);
          }
        });
      }
    }
  }, [userProfile, user]);

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' },
    { value: 'other', label: 'Other' }
  ];

  const relationshipStatusOptions = [
    { value: 'dating', label: 'Dating' },
    { value: 'committed', label: 'Committed Relationship' },
    { value: 'engaged', label: 'Engaged' },
    { value: 'married', label: 'Married' },
    { value: 'other', label: 'Other' }
  ];

  const durationOptions = [
    { value: 'less-than-6-months', label: 'Less than 6 months' },
    { value: '6-months-1-year', label: '6 months - 1 year' },
    { value: '1-3-years', label: '1-3 years' },
    { value: '3-years', label: '3+ years' }
  ];

  // Real-time field validation
  const validateField = (name, value) => {
    let error = '';
    
    switch(name) {
      case 'firstName':
        if (!value?.trim()) {
          error = 'First name is required';
        } else if (value?.trim()?.length < 2) {
          error = 'First name must be at least 2 characters';
        }
        break;
      case 'lastName':
        if (!value?.trim()) {
          error = 'Last name is required';
        } else if (value?.trim()?.length < 2) {
          error = 'Last name must be at least 2 characters';
        }
        break;
      case 'dateOfBirth':
        if (!value) {
          error = 'Date of birth is required';
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today?.getFullYear() - birthDate?.getFullYear();
          if (age < 18) {
            error = 'You must be at least 18 years old';
          } else if (age > 120) {
            error = 'Please enter a valid date of birth';
          }
        }
        break;
      case 'gender':
        if (!value) {
          error = 'Gender is required';
        }
        break;
      case 'relationshipStatus':
        if (!value) {
          error = 'Relationship status is required';
        }
        break;
      case 'relationshipDuration':
        if (!value) {
          error = 'Relationship duration is required';
        }
        break;
    }
    
    return error;
  };

  // Pre-save validation
  const validateAllFields = () => {
    const newErrors = {};
    const fieldsToValidate = ['firstName', 'lastName', 'dateOfBirth', 'gender', 'relationshipStatus', 'relationshipDuration'];
    
    fieldsToValidate?.forEach(field => {
      let error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    onChange?.();
    
    // Real-time validation
    let error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
      setValidationStatus(prev => ({ ...prev, [name]: 'invalid' }));
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
      setValidationStatus(prev => ({ ...prev, [name]: 'valid' }));
    }
    
    // Clear save status when user makes changes
    setSaveStatus(null);
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    onChange?.();
    
    // Real-time validation for select fields
    let error = validateField(name, value);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
      setValidationStatus(prev => ({ ...prev, [name]: 'invalid' }));
    } else {
      setErrors(prev => ({ ...prev, [name]: '' }));
      setValidationStatus(prev => ({ ...prev, [name]: 'valid' }));
    }
    
    // Clear save status when user makes changes
    setSaveStatus(null);
  };

  // Post-save verification - fetch data from database to confirm persistence
  const verifyDataPersistence = async (userId) => {
    console.log('🔍 Verifying data persistence for user:', userId);
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) {
        console.error('❌ Verification failed:', error);
        return { success: false, error };
      }
      
      console.log('✅ Verification successful - Data in database:', data);
      
      // Compare saved data with what we sent
      const fullName = `${formData?.firstName} ${formData?.lastName}`?.trim();
      const verificationResults = {
        full_name: data?.full_name === fullName,
        date_of_birth: data?.date_of_birth === formData?.dateOfBirth,
        gender: data?.gender === formData?.gender,
        relationship_status: data?.relationship_status === formData?.relationshipStatus,
        relationship_duration: data?.relationship_duration === formData?.relationshipDuration
      };
      
      console.log('📊 Field-by-field verification:', verificationResults);
      
      const allFieldsMatch = Object.values(verificationResults)?.every(result => result === true);
      
      return { 
        success: allFieldsMatch, 
        data, 
        verificationResults,
        message: allFieldsMatch 
          ? 'All fields verified successfully' 
          : 'Some fields did not persist correctly'
      };
    } catch (error) {
      console.error('❌ Verification exception:', error);
      return { success: false, error };
    }
  };

  const handleSaveProfile = async () => {
    console.log('💾 Starting profile save operation');
    console.log('📝 Current form data:', formData);
    
    // Pre-save validation
    setSaveStatus({ type: 'validating', message: 'Validating fields...' });
    
    if (!validateAllFields()) {
      console.warn('⚠️ Validation failed');
      setSaveStatus({ type: 'error', message: 'Please fix validation errors before saving' });
      notificationManager?.error('Please fix validation errors before saving');
      return;
    }
    
    console.log('✅ Pre-save validation passed');
    
    setIsSaving(true);
    setSaveStatus({ type: 'saving', message: 'Saving profile...' });
    
    try {
      const fullName = `${formData?.firstName} ${formData?.lastName}`?.trim();
      const updates = { 
        full_name: fullName,
        date_of_birth: formData?.dateOfBirth,
        gender: formData?.gender,
        relationship_status: formData?.relationshipStatus,
        relationship_duration: formData?.relationshipDuration
      };
      
      console.log('📤 Sending updates to database:', updates);
      
      // updateProfile now handles retries internally
      const result = await updateProfile(updates);
      
      console.log('📥 Database response:', result);
      
      if (result?.error) {
        console.error('❌ Save failed:', result?.error);
        setSaveStatus({ 
          type: 'error', 
          message: `Failed to save: ${result?.error?.message || 'Unknown error'}` 
        });
        return;
      }
      
      console.log('✅ Save successful, starting verification...');
      setSaveStatus({ type: 'verifying', message: 'Verifying data persistence...' });
      
      // Wait a moment for database to fully commit
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Post-save verification
      const verification = await verifyDataPersistence(user?.id);
      
      if (verification?.success) {
        console.log('✅ All fields verified successfully!');
        setSaveStatus({ 
          type: 'success', 
          message: '✓ Profile saved and verified successfully! All fields persisted correctly.',
          details: verification?.verificationResults
        });
      } else {
        console.warn('⚠️ Verification detected issues:', verification);
        setSaveStatus({ 
          type: 'warning', 
          message: 'Profile saved but verification found inconsistencies. Please refresh and check your data.',
          details: verification?.verificationResults
        });
        notificationManager?.warning('Profile saved but some fields may need verification');
      }
    } catch (error) {
      console.error('❌ Exception during save:', error);
      setSaveStatus({ 
        type: 'error', 
        message: `Error: ${error?.message || 'Unknown error occurred'}` 
      });
      notificationManager?.error(`Save failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
      console.log('🏁 Save operation completed');
    }
  };

  const validatePasswordChange = () => {
    const newErrors = {};
    if (!formData?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!formData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    if (formData?.newPassword !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePasswordUpdate = () => {
    if (validatePasswordChange()) {
      alert('Password updated successfully!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setShowPasswordSection(false);
    }
  };

  // Get status indicator for field
  const getFieldStatus = (fieldName) => {
    if (errors?.[fieldName]) return 'error';
    if (validationStatus?.[fieldName] === 'valid') return 'success';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Save Status Banner */}
      {saveStatus && (
        <div className={`p-4 rounded-lg border ${
          saveStatus?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
          saveStatus?.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
          saveStatus?.type === 'warning'? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {saveStatus?.type === 'success' && <span className="text-xl">✓</span>}
              {saveStatus?.type === 'error' && <span className="text-xl">✗</span>}
              {saveStatus?.type === 'warning' && <span className="text-xl">⚠</span>}
              {(saveStatus?.type === 'saving' || saveStatus?.type === 'validating' || saveStatus?.type === 'verifying') && (
                <span className="text-xl">⟳</span>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">{saveStatus?.message}</p>
              {saveStatus?.details && (
                <div className="mt-2 text-sm">
                  <p className="font-semibold">Field Verification:</p>
                  <ul className="mt-1 space-y-1">
                    <li>Name: {saveStatus?.details?.full_name ? '✓' : '✗'}</li>
                    <li>Date of Birth: {saveStatus?.details?.date_of_birth ? '✓' : '✗'}</li>
                    <li>Gender: {saveStatus?.details?.gender ? '✓' : '✗'}</li>
                    <li>Relationship Status: {saveStatus?.details?.relationship_status ? '✓' : '✗'}</li>
                    <li>Relationship Duration: {saveStatus?.details?.relationship_duration ? '✓' : '✗'}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="First Name"
              name="firstName"
              value={formData?.firstName}
              onChange={handleChange}
              required
              error={errors?.firstName}
            />
            {validationStatus?.firstName === 'valid' && !errors?.firstName && (
              <p className="text-xs text-green-600 mt-1">✓ Valid</p>
            )}
          </div>
          <div>
            <Input
              label="Last Name"
              name="lastName"
              value={formData?.lastName}
              onChange={handleChange}
              required
              error={errors?.lastName}
            />
            {validationStatus?.lastName === 'valid' && !errors?.lastName && (
              <p className="text-xs text-green-600 mt-1">✓ Valid</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Input
              label="Date of Birth"
              type="date"
              name="dateOfBirth"
              value={formData?.dateOfBirth}
              onChange={handleChange}
              required
              error={errors?.dateOfBirth}
            />
            {validationStatus?.dateOfBirth === 'valid' && !errors?.dateOfBirth && (
              <p className="text-xs text-green-600 mt-1">✓ Valid</p>
            )}
          </div>
          <div>
            <Select
              label="Gender"
              name="gender"
              value={formData?.gender}
              onChange={(value) => handleSelectChange('gender', value)}
              options={genderOptions}
              required
              error={errors?.gender}
            />
            {validationStatus?.gender === 'valid' && !errors?.gender && (
              <p className="text-xs text-green-600 mt-1">✓ Valid</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveProfile}
            loading={isSaving}
            disabled={Object.keys(errors)?.some(key => errors?.[key])}
          >
            {isSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>
      <div>
        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData?.email}
          onChange={handleChange}
          description="This email is used for login and notifications"
          disabled
          required
        />
      </div>
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Relationship Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Relationship Status"
            name="relationshipStatus"
            value={formData?.relationshipStatus}
            onChange={(value) => handleSelectChange('relationshipStatus', value)}
            options={relationshipStatusOptions}
            required
          />
          <Select
            label="Relationship Duration"
            name="relationshipDuration"
            value={formData?.relationshipDuration}
            onChange={(value) => handleSelectChange('relationshipDuration', value)}
            options={durationOptions}
            required
          />
        </div>
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveProfile}
            loading={isSaving}
          >
            Save Relationship Info
          </Button>
        </div>
      </div>
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Password & Security
          </h3>
          {!showPasswordSection && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordSection(true)}
            >
              Change Password
            </Button>
          )}
        </div>

        {showPasswordSection && (
          <div className="space-y-4 bg-muted/30 p-4 rounded-lg border border-border">
            <Input
              label="Current Password"
              type="password"
              name="currentPassword"
              value={formData?.currentPassword}
              onChange={handleChange}
              error={errors?.currentPassword}
              required
            />
            <Input
              label="New Password"
              type="password"
              name="newPassword"
              value={formData?.newPassword}
              onChange={handleChange}
              error={errors?.newPassword}
              description="Must be at least 8 characters"
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              name="confirmPassword"
              value={formData?.confirmPassword}
              onChange={handleChange}
              error={errors?.confirmPassword}
              required
            />
            <div className="flex items-center space-x-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPasswordSection(false);
                  setFormData(prev => ({
                    ...prev,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  }));
                  setErrors({});
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePasswordUpdate}
              >
                Update Password
              </Button>
            </div>
          </div>
        )}

        {!showPasswordSection && (
          <p className="text-sm text-muted-foreground">
            Last changed 3 months ago
          </p>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;